import { useTranslation } from "react-i18next";
import { cmsUrl } from "@kozien/shared";
import { cn } from "@/lib/cn";
import { discountPercentage, formatPrice } from "@/lib/formatters";
import { ColorSwatch } from "@/components/molecules/ColorSwatch/ColorSwatch";
import { FabricTypeSelector } from "@/components/molecules/FabricTypeSelector/FabricTypeSelector";
import { Badge } from "@/components/atoms/Badge/Badge";
import { findColorByHex } from "@/models/color/findColorByHex";
import { resolveDecorationImageUrl } from "@/models/color/resolveDecorationImageUrl";
import type { Product } from "@/models/product";
import type { ControlType, WindowFabricColor } from "@/store/selection";
import type { DealProductWithLocation } from "@/models/deal/deal.types";

type FabricType = "TRANSLUCENT" | "SUN_BLOCKING";

const FABRIC_TYPE_ORDER: FabricType[] = ["TRANSLUCENT", "SUN_BLOCKING"];
const CONTROL_TYPE_ORDER: ControlType[] = ["REMOTE", "APP"];

interface WindowCardProps {
    windowId: string;
    dealProduct: DealProductWithLocation;
    product: Product;
    storedSelection: WindowFabricColor | undefined;
    isOpen: boolean;
    isLast: boolean;
    onToggle: () => void;
    onColorSelected: (
        colorId: string,
        fabricType: FabricType,
        frameColorId: string | null,
    ) => void;
    onControlTypeChange: (controlType: ControlType) => void;
    onNext: () => void;
    cardRef: (el: HTMLDivElement | null) => void;
    price: number | null;
    /** Net price after a valid discount code; renders the strikethrough +
     * red percentage pill when it's lower than `price`. */
    discountedPrice?: number | null;
}

export function WindowCard({
    dealProduct,
    product,
    storedSelection,
    isOpen,
    isLast,
    onToggle,
    onColorSelected,
    onControlTypeChange,
    onNext,
    cardRef,
    price,
    discountedPrice,
}: WindowCardProps) {
    const { t } = useTranslation();

    const availableTypes = FABRIC_TYPE_ORDER.filter((type) =>
        product.fabricColors.some((c) => c.type === type),
    );
    const showFabricTypeSelector = availableTypes.length > 1;
    const showControlTypeSelector = product.productType === "SUN_PROTECTION";
    const controlType: ControlType = storedSelection?.controlType ?? "REMOTE";

    const fabricType: FabricType =
        storedSelection?.fabricType ?? availableTypes[0] ?? "TRANSLUCENT";

    const filteredColors = showFabricTypeSelector
        ? product.fabricColors.filter((c) => c.type === fabricType)
        : product.fabricColors;

    const selectedColor =
        product.fabricColors.find((c) => c.id === storedSelection?.colorId) ??
        null;

    function handleFabricTypeChange(newType: FabricType) {
        if (newType === fabricType) return;
        const newFiltered = product.fabricColors.filter(
            (c) => c.type === newType,
        );
        if (newFiltered.length === 0) return;

        const matchByCombination =
            product.hasFrameColor && storedSelection?.frameColorName
                ? newFiltered.find(
                      (c) =>
                          c.name &&
                          product.colorCombinations.some(
                              (cc) =>
                                  !cc.irrelevant &&
                                  cc.fabricColorName === c.name &&
                                  cc.frameColorName ===
                                      storedSelection.frameColorName,
                          ),
                  )
                : undefined;
        const next = matchByCombination ?? newFiltered[0];
        if (!next?.id) return;
        onColorSelected(
            next.id,
            newType,
            storedSelection?.frameColorId ?? null,
        );
    }

    const thumbnailUrl = dealProduct.previewImage?.url
        ? cmsUrl(
              dealProduct.previewImage.url,
              import.meta.env.VITE_CMS_BASE_ENDPOINT,
          )
        : null;

    const decorationImageUrl =
        cmsUrl(
            resolveDecorationImageUrl(
                product,
                storedSelection?.colorId ?? null,
                storedSelection?.frameColorId ?? null,
            ),
            import.meta.env.VITE_CMS_BASE_ENDPOINT,
        ) ?? null;

    const fabricLabels: Record<FabricType, string> = {
        TRANSLUCENT: t("colorStep.fabricType.translucent"),
        SUN_BLOCKING: t("colorStep.fabricType.sunBlocking"),
    };

    const controlLabels: Record<ControlType, string> = {
        REMOTE: t("colorStep.controlType.remote"),
        APP: t("colorStep.controlType.app"),
    };

    const requiresFrame = product.hasFrameColor;
    const hasFabric = !!storedSelection?.colorId;
    const hasFrame = !!storedSelection?.frameColorId;
    const isComplete = hasFabric && (!requiresFrame || hasFrame);

    // Frame click → pick the fabric whose hex matches the new frame; fall
    // back to the user's existing fabric when no same-hex fabric exists.
    function handleFrameSelected(frameId: string) {
        const frame = product.frameColors.find((f) => f.id === frameId);
        const matchByHex = findColorByHex(
            product.fabricColors,
            frame?.hexColor,
        );
        const currentFabric = product.fabricColors.find(
            (c) => c.id === storedSelection?.colorId,
        );
        const fabric = matchByHex ?? currentFabric;
        if (!fabric?.id) return;
        onColorSelected(fabric.id, fabric.type ?? fabricType, frameId);
    }

    // Fabric click → pick the frame whose hex matches the new fabric; fall
    // back to the user's existing frame when no same-hex frame exists.
    function handleFabricSelected(fabricColorId: string) {
        const fabric = product.fabricColors.find((c) => c.id === fabricColorId);
        if (!fabric?.id) return;
        const matchByHex = product.hasFrameColor
            ? findColorByHex(product.frameColors, fabric.hexColor)
            : null;
        const currentFrame = product.frameColors.find(
            (f) => f.id === storedSelection?.frameColorId,
        );
        const frame = matchByHex ?? currentFrame ?? null;
        onColorSelected(
            fabric.id,
            fabric.type ?? fabricType,
            frame?.id ?? null,
        );
    }

    return (
        <div
            ref={cardRef}
            data-testid="color-step-window"
            className={cn(
                "rounded-xl border bg-white overflow-hidden transition-all duration-200",
                isOpen
                    ? "border-primary border-2 shadow-lg ring-2 ring-primary/15"
                    : "border-gray-200",
            )}
        >
            <button
                type="button"
                onClick={onToggle}
                data-testid="window-card-header"
                className="w-full flex items-center gap-2 sm:gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors cursor-pointer"
            >
                <div className="size-14 sm:size-20 rounded-md overflow-hidden bg-gray-100 shrink-0">
                    {thumbnailUrl ? (
                        <img
                            src={thumbnailUrl}
                            alt={selectedColor?.name ?? product.name ?? ""}
                            className="h-full w-full object-cover"
                        />
                    ) : (
                        <div className="h-full w-full bg-gray-200" />
                    )}
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                    <p className="min-w-0 flex-1 truncate text-base sm:text-2xl font-semibold text-gray-900">
                        {dealProduct.locationName}
                    </p>
                    {price !== null &&
                    discountedPrice != null &&
                    discountedPrice < price ? (
                        <div
                            data-testid="window-card-price"
                            className="flex shrink-0 flex-col items-start sm:items-end"
                        >
                            <div className="flex items-center gap-1.5">
                                <Badge
                                    variant="danger"
                                    label={`-${discountPercentage(price, discountedPrice)}%`}
                                />
                                <del className="text-sm text-gray-400">
                                    {formatPrice(price)}
                                </del>
                            </div>
                            <span className="text-lg sm:text-2xl font-semibold text-gray-800">
                                {formatPrice(discountedPrice)}
                            </span>
                        </div>
                    ) : (
                        price !== null && (
                            <span
                                data-testid="window-card-price"
                                className="shrink-0 text-lg sm:text-2xl font-semibold text-gray-800"
                            >
                                {formatPrice(price)}
                            </span>
                        )
                    )}
                </div>
                {isComplete && <Badge label={t("colorStep.completed")} />}
                <svg
                    className={cn(
                        "size-4 shrink-0 text-gray-400 transition-transform duration-300",
                        isOpen ? "rotate-180" : "",
                    )}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19 9l-7 7-7-7"
                    />
                </svg>
            </button>

            <div
                data-testid="window-card-body"
                className={cn(
                    "grid transition-all duration-400 ease-in-out",
                    isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
                )}
            >
                <div className="overflow-hidden">
                    <div className="border-t border-gray-100 px-4 pb-4 pt-3 flex flex-col gap-4">
                        <div className="w-full rounded-lg overflow-hidden bg-white sm:h-100">
                            {decorationImageUrl ? (
                                <img
                                    src={decorationImageUrl}
                                    alt={
                                        selectedColor?.name ??
                                        product.name ??
                                        ""
                                    }
                                    className="h-auto w-full object-contain sm:h-full"
                                />
                            ) : (
                                <div className="h-100 w-full bg-gray-200" />
                            )}
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-start gap-4 py-5 lg:w-1/2">
                            {showFabricTypeSelector && (
                                <FabricTypeSelector
                                    options={availableTypes}
                                    labels={fabricLabels}
                                    value={fabricType}
                                    onChange={handleFabricTypeChange}
                                    title={`1. ${t("colorStep.fabricType.label")}`}
                                />
                            )}
                            <div className="flex flex-col flex-basis-0 grow gap-4">
                                <div className="flex flex-col gap-2">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                                        {showFabricTypeSelector ? "2." : "1."}{" "}
                                        {t("colorStep.colorCollection")}
                                    </p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {filteredColors.map((color) => (
                                            <ColorSwatch
                                                key={color.id}
                                                hexColor={color.hexColor}
                                                name={color.name}
                                                selected={
                                                    storedSelection?.colorId ===
                                                    color.id
                                                }
                                                onClick={() =>
                                                    color.id &&
                                                    handleFabricSelected(
                                                        color.id,
                                                    )
                                                }
                                                size="sm"
                                            />
                                        ))}
                                    </div>
                                </div>
                                {product.hasFrameColor && (
                                    <div className="flex flex-col gap-2">
                                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                                            {showFabricTypeSelector
                                                ? "3."
                                                : "2."}{" "}
                                            {t(
                                                "colorStep.frameColorCollection",
                                            )}
                                        </p>
                                        <div className="flex flex-wrap gap-1.5">
                                            {product.frameColors.map(
                                                (frame) => (
                                                    <ColorSwatch
                                                        key={frame.id}
                                                        hexColor={
                                                            frame.hexColor
                                                        }
                                                        name={frame.name}
                                                        selected={
                                                            storedSelection?.frameColorId ===
                                                            frame.id
                                                        }
                                                        onClick={() =>
                                                            handleFrameSelected(
                                                                frame.id,
                                                            )
                                                        }
                                                        size="sm"
                                                    />
                                                ),
                                            )}
                                        </div>
                                    </div>
                                )}
                                {showControlTypeSelector && (
                                    <FabricTypeSelector
                                        options={CONTROL_TYPE_ORDER}
                                        labels={controlLabels}
                                        value={controlType}
                                        onChange={onControlTypeChange}
                                        title={`${
                                            (showFabricTypeSelector ? 1 : 0) +
                                            (product.hasFrameColor ? 1 : 0) +
                                            2
                                        }. ${t("colorStep.controlType.label")}`}
                                    />
                                )}
                            </div>
                        </div>
                        {!isLast && (
                            <button
                                type="button"
                                data-testid="window-card-next"
                                disabled={!isComplete}
                                onClick={onNext}
                                className={cn(
                                    "w-full rounded-lg py-2 text-sm font-semibold transition-colors",
                                    isComplete
                                        ? "bg-primary text-white cursor-pointer"
                                        : "bg-gray-100 text-gray-400 cursor-not-allowed",
                                )}
                            >
                                {t("colorStep.next")}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
